'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { api } from '@/lib/api';

interface Location {
  id: string;
  name: string;
  type: 'state' | 'district' | 'town';
  full_name: string;
  district?: string;
  state?: string;
}

interface FilterSectionProps {
  onFilterChange: (filters: FilterState) => void;
  initialArea?: string;
  initialAreaType?: string;
}

interface FilterState {
  searchQuery: string;
  selectedArea: string;
  selectedAreaType: string;
  selectedService: string;
}

interface Service {
  id: number;
  name: string;
  slug: string;
}

const FilterSection = ({ onFilterChange, initialArea = '', initialAreaType = '' }: FilterSectionProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const [isHydrated, setIsHydrated] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    selectedArea: '',
    selectedAreaType: '',
    selectedService: '',
  });

  // Services from database
  const [services, setServices] = useState<Service[]>([]);

  // Location autocomplete state
  const [locationQuery, setLocationQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Location[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get<{ locations: Location[] }>(
        `/locations/search?q=${encodeURIComponent(query)}`
      );
      setSuggestions(response.locations);
      setIsOpen(response.locations.length > 0);
      setHighlightedIndex(-1);
    } catch {
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch services from database
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await api.get<{ services: Service[] }>('/services');
        setServices(response.services);
      } catch {
        console.error('Failed to fetch services');
        setServices([]);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    setIsHydrated(true);

    // Only apply filter if there's an initial area from URL params
    if (initialArea) {
      setLocationQuery(initialArea);
      const newFilters: FilterState = {
        searchQuery: '',
        selectedArea: initialArea,
        selectedAreaType: initialAreaType,
        selectedService: '',
      };
      setFilters(newFilters);
      onFilterChange(newFilters);
    }
    // Otherwise, start with empty filters (show all agents)
  }, [initialArea, initialAreaType]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      // Only fetch if the query differs from selected area (user is typing new search)
      if (locationQuery && locationQuery !== filters.selectedArea) {
        fetchSuggestions(locationQuery);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [locationQuery, fetchSuggestions, filters.selectedArea]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterChange = (key: keyof FilterState, value: string, areaType?: string) => {
    const newFilters = {
      ...filters,
      [key]: value,
      ...(areaType !== undefined && { selectedAreaType: areaType }),
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleLocationSelect = (location: Location) => {
    setLocationQuery(location.name);
    handleFilterChange('selectedArea', location.name, location.type);
    setIsOpen(false);
    setSuggestions([]);
  };

  const handleLocationInputChange = (value: string) => {
    setLocationQuery(value);
    if (!value) {
      handleFilterChange('selectedArea', '', '');
    }
  };

  const handleLocationKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleLocationSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const clearFilters = () => {
    const emptyFilters: FilterState = {
      searchQuery: '',
      selectedArea: '',
      selectedAreaType: '',
      selectedService: '',
    };
    setFilters(emptyFilters);
    setLocationQuery('');
    onFilterChange(emptyFilters);

    // Clear URL search params
    router.replace(pathname, { scroll: false });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'state':
        return 'Negeri';
      case 'district':
        return 'Daerah';
      case 'town':
        return 'Bandar';
      default:
        return '';
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-2xl shadow-md p-6 mb-6">
        <div className="animate-pulse space-y-4">
          <div className="h-14 bg-muted rounded-xl"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-14 bg-muted rounded-xl"></div>
            <div className="h-14 bg-muted rounded-xl"></div>
            <div className="h-14 bg-muted rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="bg-card p-4 md:p-6 rounded-2xl shadow-xl border border-border mb-6">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
        <div className="md:col-span-5 relative group">
          <Icon
            name="MagnifyingGlassIcon"
            size={20}
            variant="outline"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-textSecondary group-focus-within:text-primary"
          />
          <input
            className="w-full pl-12 pr-4 h-14 bg-muted border-none rounded-xl focus:ring-2 focus:ring-primary/20 font-medium transition-all"
            placeholder="Cari nama agent..."
            type="text"
            value={filters.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
          />
        </div>

        {/* Location Autocomplete */}
        <div className="md:col-span-3 relative" ref={wrapperRef}>
          <Icon
            name="MapPinIcon"
            size={20}
            variant="outline"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-textSecondary pointer-events-none z-10"
          />
          <input
            className="w-full pl-12 pr-10 h-14 bg-muted border-none rounded-xl focus:ring-2 focus:ring-primary/20 font-medium transition-all"
            placeholder="Cari kawasan..."
            type="text"
            value={locationQuery}
            onChange={(e) => handleLocationInputChange(e.target.value)}
            onFocus={() => locationQuery.length >= 2 && suggestions.length > 0 && setIsOpen(true)}
            onKeyDown={handleLocationKeyDown}
          />
          {isLoading && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
            </div>
          )}

          {/* Suggestions Dropdown */}
          {isOpen && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg max-h-64 overflow-y-auto z-50">
              {suggestions.map((location, index) => (
                <button
                  key={location.id}
                  type="button"
                  onClick={() => handleLocationSelect(location)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors ${
                    index === highlightedIndex ? 'bg-muted' : 'hover:bg-muted/50'
                  } ${index !== suggestions.length - 1 ? 'border-b border-border/50' : ''}`}
                >
                  <Icon
                    name="MapPinIcon"
                    size={16}
                    variant="solid"
                    className="text-accent shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-textPrimary text-sm truncate">
                        {location.name}
                      </span>
                      <span
                        className={`text-[9px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                          location.type === 'state'
                            ? 'bg-blue-100 text-blue-700'
                            : location.type === 'district'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {getTypeLabel(location.type)}
                      </span>
                    </div>
                    {location.type !== 'state' && (
                      <p className="text-xs text-textSecondary truncate">
                        {location.type === 'town'
                          ? `${location.district}, ${location.state}`
                          : location.state}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-3 relative">
          <Icon
            name="SparklesIcon"
            size={20}
            variant="outline"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-textSecondary pointer-events-none"
          />
          <select
            className="w-full pl-12 pr-4 h-14 bg-muted border-none rounded-xl font-bold text-textPrimary focus:ring-2 focus:ring-primary/20 appearance-none"
            value={filters.selectedService}
            onChange={(e) => handleFilterChange('selectedService', e.target.value)}
          >
            <option value="">Semua Servis</option>
            {services.map((service) => (
              <option key={service.id} value={service.name}>
                {service.name}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-1">
          <button
            onClick={clearFilters}
            className="w-full h-14 flex items-center justify-center text-textSecondary hover:text-primary hover:bg-muted border border-transparent hover:border-border rounded-xl transition-all"
            title="Kosongkan Tapis"
          >
            <Icon name="ArrowPathIcon" size={22} variant="outline" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default FilterSection;
